import { html, nothing } from "lit";
import "../components/plan-checklist.ts";
import {
  AGENT_PLANS_STATUS_FILTERS,
  type AgentPlanEntry,
  type AgentPlansStatusFilter,
} from "../controllers/agent-plans.ts";

export type AgentPlansProps = {
  loading: boolean;
  plans: AgentPlanEntry[] | null;
  error: string | null;
  statusFilter: AgentPlansStatusFilter;
  onStatusFilterChange: (filter: AgentPlansStatusFilter) => void;
  onRefresh: () => void;
};

function statusChipClass(status: AgentPlanEntry["status"]): string {
  switch (status) {
    case "active":
      return "chip chip-ok";
    case "abandoned":
      return "chip chip-warn";
    default:
      return "chip";
  }
}

function truncateKey(key: string, max = 28): string {
  return key.length > max ? `${key.slice(0, max)}…` : key;
}

export function renderAgentPlans(props: AgentPlansProps) {
  const plans = props.plans ?? [];
  return html`
    <div class="card" style="overflow-x:auto">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">Agent Plans</div>
          <div class="card-sub">
            Plan checklists projected from agent update_plan calls, newest first.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <select
            class="btn"
            style="font-size:0.85em;padding:4px 8px;border-radius:6px;"
            .value=${props.statusFilter}
            @change=${(e: Event) =>
              props.onStatusFilterChange(
                (e.target as HTMLSelectElement).value as AgentPlansStatusFilter,
              )}
          >
            ${AGENT_PLANS_STATUS_FILTERS.map(
              (f) => html`<option value=${f.value} ?selected=${props.statusFilter === f.value}>
                ${f.label}
              </option>`,
            )}
          </select>
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      ${props.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
        : nothing}

      ${!props.plans
        ? html`<div class="muted" style="margin-top: 12px;">
            ${props.loading ? "Loading plans…" : "Click Refresh to load plans."}
          </div>`
        : nothing}

      ${props.plans
        ? html`
            <table class="settings-table" style="margin-top: 16px; width: 100%;">
              <thead>
                <tr>
                  <th style="text-align: left; min-width: 240px;">Title</th>
                  <th style="white-space: nowrap;">Progress</th>
                  <th>Status</th>
                  <th style="text-align: left;">Session</th>
                  <th style="text-align: left; white-space: nowrap;">Updated</th>
                </tr>
              </thead>
              <tbody>
                ${plans.length === 0
                  ? html`<tr><td colspan="5" class="muted">No plans found.</td></tr>`
                  : plans.map((plan) => renderPlanRow(plan))}
              </tbody>
            </table>
            <p class="muted" style="margin-top: 0.75rem;">
              ${plans.length} plan${plans.length === 1 ? "" : "s"} · newest 100 max
            </p>
          `
        : nothing}
    </div>
  `;
}

function renderPlanRow(plan: AgentPlanEntry) {
  const steps = plan.steps ?? [];
  const done = steps.filter((s) => s.status === "done" || s.status === "skipped").length;
  const title = plan.title || "Untitled plan";
  return html`
    <tr>
      <td style="text-align: left;">
        ${steps.length > 0
          ? html`<details>
              <summary style="cursor: pointer;">${title}</summary>
              <ig-plan-checklist .planTitle=${title} .steps=${steps}></ig-plan-checklist>
            </details>`
          : html`<span>${title}</span>`}
      </td>
      <td style="text-align: center; white-space: nowrap;">${done}/${steps.length}</td>
      <td style="text-align: center;">
        <span class=${statusChipClass(plan.status)}>${plan.status}</span>
      </td>
      <td class="muted" style="font-size: 0.85em;">
        <code title=${plan.sessionKey}>${truncateKey(plan.sessionKey)}</code>
      </td>
      <td class="muted" style="font-size: 0.85em; white-space: nowrap;">
        ${new Date(plan.updatedAt).toLocaleString()}
      </td>
    </tr>
  `;
}
